/**
 * Flip toast notifications class
 */
class FlipNotifications {
  /**
   * Params is an object which can accept fields:
   *
   * message: required parameter, notification message
   * url: if set this will add >>View anchor that will redirect you to given url
   * duration: notification showing duration
   * attach: the side of notification attachment - can accept string param of 'left' or 'right'
   * type: type of notification - can accept string param of 'notification', 'error', 'warning', 'success'
   *
   * @param params
   */
  constructor(params) {
    if (!params || !params.message) {
      throw Error('Message param is required!');
    } else if (typeof params.message !== 'string' && typeof params.message !== 'number') {
      throw Error(`Message can't accept type ${typeof params.message}`);
    }

    this.message = params.message;
    this.duration = parseInt(params.duration) || 5000;
    this.attachment_direction =
      params.attach && ['left', 'right'].indexOf(params.attach) !== -1 ? params.attach : 'right';
    this.url = params.url || null;
    this.type =
      params.type && ['error', 'notification', 'warning', 'success'].indexOf(params.type) !== -1
        ? params.type
        : 'notification';
    this.wrapper = document.createElement('div');

    /**
     * Resize event listener function
     * stored to remove it on dismiss
     */
    this.listener = function () {
      this.resetCurrentNotificationOffsetHeight();
    }.bind(this);
    /**
     * Add event listener to window resize
     * on window resize - recalculate notifications offset height
     */
    window.addEventListener('resize', this.listener);
  }

  /**
   * Show method appends notification wrapper to body
   */
  show() {
    /**
     * Generate notification html code
     */
    this.setUpWrapperHtml();
    /**
     * Add notification to DOM
     */
    document.body.appendChild(this.wrapper);
    /**
     * Calculate notification offset height
     */
    this.resetCurrentNotificationOffsetHeight();
  }

  /**
   * Setting up wrapper InnerHTML out
   */
  setUpWrapperHtml() {
    this.wrapper.className = `notification-wrapper is-animated is-attached-${this.attachment_direction} is-active is-${this.type}`;
    this.wrapper.innerHTML = this.type ? `<aside class="icon-wrapper">${this.getTypeIconCode()}</aside>` : ``;
    this.wrapper.innerHTML += this.url
      ? ` <aside class="text"><p>${this.message}</p><p class="url-wrapper"><a href="${this.url}">View</a></p></aside>`
      : ` <aside class="text"><p>${this.message}</p></aside>`;
    this.setUpDismissButton();
  }

  /**
   * Get svg icon out of notification type
   *
   * @returns {string}
   */
  getTypeIconCode() {
    switch (this.type) {
      case 'warning':
        return `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="40" height="40" rx="20" fill="#F97316"/>
</svg><svg class="secondary-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-alert-icon lucide-circle-alert"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>`;
      case 'error':
        return `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="40" height="40" rx="20" fill="#B71C1C"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M27.825 12.1747C28.2806 12.6303 28.2806 13.369 27.825 13.8246L13.825 27.8246C13.3694 28.2802 12.6307 28.2802 12.1751 27.8246C11.7194 27.369 11.7194 26.6303 12.1751 26.1747L26.1751 12.1747C26.6307 11.7191 27.3694 11.7191 27.825 12.1747Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.1751 12.1747C12.6307 11.7191 13.3694 11.7191 13.825 12.1747L27.825 26.1747C28.2806 26.6303 28.2806 27.369 27.825 27.8246C27.3694 28.2802 26.6307 28.2802 26.1751 27.8246L12.1751 13.8246C11.7194 13.369 11.7194 12.6303 12.1751 12.1747Z" fill="white"/>
</svg>`;
      case 'success':
        return `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="40" height="40" rx="20" fill="#16A34A"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M30.1583 12.1747C30.6139 12.6303 30.6139 13.369 30.1583 13.8246L17.325 26.658C16.8693 27.1136 16.1307 27.1136 15.675 26.658L9.84171 20.8246C9.3861 20.369 9.3861 19.6303 9.84171 19.1747C10.2973 18.7191 11.036 18.7191 11.4916 19.1747L16.5 24.1831L28.5084 12.1747C28.964 11.7191 29.7027 11.7191 30.1583 12.1747Z" fill="white"/>
</svg>`;
      case 'online':
        return `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="40" height="40" rx="20" fill="#16A34A"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M30.1583 12.1747C30.6139 12.6303 30.6139 13.369 30.1583 13.8246L17.325 26.658C16.8693 27.1136 16.1307 27.1136 15.675 26.658L9.84171 20.8246C9.3861 20.369 9.3861 19.6303 9.84171 19.1747C10.2973 18.7191 11.036 18.7191 11.4916 19.1747L16.5 24.1831L28.5084 12.1747C28.964 11.7191 29.7027 11.7191 30.1583 12.1747Z" fill="white"/>
</svg>`;
      default:
        return '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 459.334 459.334" style="enable-background:new 0 0 459.334 459.334;" xml:space="preserve"><g><path d="M175.216,404.514c-0.001,0.12-0.009,0.239-0.009,0.359c0,30.078,24.383,54.461,54.461,54.461 s54.461-24.383,54.461-54.461c0-0.12-0.008-0.239-0.009-0.359H175.216z"/><path d="M403.549,336.438l-49.015-72.002c0-22.041,0-75.898,0-89.83c0-60.581-43.144-111.079-100.381-122.459V24.485 C254.152,10.963,243.19,0,229.667,0s-24.485,10.963-24.485,24.485v27.663c-57.237,11.381-100.381,61.879-100.381,122.459 c0,23.716,0,76.084,0,89.83l-49.015,72.002c-5.163,7.584-5.709,17.401-1.419,25.511c4.29,8.11,12.712,13.182,21.887,13.182 H383.08c9.175,0,17.597-5.073,21.887-13.182C409.258,353.839,408.711,344.022,403.549,336.438z"/></g></svg>';
    }
  }

  /**
   * Recalculate all active notifications offsets (on notification dismiss)
   */
  resetOffsetHeights() {
    let active_notifications = document.querySelectorAll(
      '.notification-wrapper.is-animated.is-active' +
        (window.innerWidth <= 800 ? `` : `.is-attached-${this.attachment_direction}`)
    );
    let offset_top = 10;
    active_notifications.forEach((item) => {
      item.style.top = offset_top + 'px';
      offset_top += item.clientHeight + 10;
    });
  }

  /**
   * Recalculate current notification offset (on notification creation or window.resize)
   */
  resetCurrentNotificationOffsetHeight() {
    let active_notifications = document.querySelectorAll(
      '.notification-wrapper.is-animated.is-active' +
        (window.innerWidth <= 800 ? `` : `.is-attached-${this.attachment_direction}`)
    );
    let offset_top = 10;
    for (let i = 0; i < active_notifications.length; i++) {
      if (active_notifications[i] === this.wrapper) {
        active_notifications[i].style.top = offset_top + 'px';
        break;
      }
      offset_top += active_notifications[i].clientHeight + 4;
    }
  }

  /**
   * Add dismiss button to notification wrapper
   * with dismiss timeout and add click event listener
   */
  setUpDismissButton() {
    let dismiss_button = document.createElement('span');
    dismiss_button.className = 'dismiss';
    dismiss_button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path fill-rule="evenodd" clip-rule="evenodd" d="M18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289Z" fill="#16161D"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z" fill="#16161D"/>
</svg>`;
    this.wrapper.appendChild(dismiss_button);

    let dismissTimeout = setTimeout(() => {
      this.dismiss();
    }, this.duration);

    dismiss_button.addEventListener(
      'click',
      () => {
        this.dismiss();
        clearTimeout(dismissTimeout);
      },
      false
    );
  }

  /**
   * Dismiss notification and delete it from DOM
   */
  dismiss() {
    this.wrapper.classList.remove('is-active');
    this.wrapper.classList.add('is-hiding');
    setTimeout(() => {
      document.body.removeChild(this.wrapper);
      this.resetOffsetHeights();
    }, 260);
    /**
     * Remove this notification resize event listener
     */
    window.removeEventListener('resize', this.listener);
  }
}

export default FlipNotifications;
